"use client";
import { Breadcrumb } from "@/components/shared";
import { ProjectService, PropertyService } from "@/services";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCompatRouter as useRouter } from "@/lib/compat-router/context";
import { axiosWithoutToken } from "@/utils/axios";
import { useEffect, useRef, useState } from "react";
import { Footer, Nav, Button } from "@/components/bnp-ui";
import { toast } from "react-toastify";
import GoogleAd from "@/components/shared/GoogleAd";
import { AdRenderer } from "@/components/shared/ads/AdRenderer";
import { usePageAds } from "@/hooks/usePageAds";
import { AdPage, AdPlacement } from "@/types/advertising";
import useAuthToken from "@/hooks/useAuthToken";
import { capitalizeFirstLetter } from "@/utils/capitalizeFirstLetter";
import parseHttpError from "@/utils/parseHttpError";
import { useCurrency } from "@/hooks/useCurrency";
import { buildActionId, startUserAction } from "@/utils/idempotency";
import { Property } from "@/types/property";
import { track } from "@/utils/track";
import { usePostHogAnalytics } from "@/contexts/PostHogAnalyticsContext";
import PropDetailHeading from "@/components/bnp-ui/property/PropDetailHeading";
import PropDetailsMedia from "@/components/bnp-ui/property/PropDetailsMedia";
import PropDetailsSemantics from "@/components/bnp-ui/property/PropDetailsSemantics";
import PropDetailsAmenities from "@/components/bnp-ui/property/PropDetailsAmenities";
import PropDetailsAdditionalInfo from "@/components/bnp-ui/property/PropDetailsAdditionalInfo";
import PropDetailsMap from "@/components/bnp-ui/property/PropDetailsMap";
import PropDetailsPriceBreakdown from "@/components/bnp-ui/property/PropDetailsPriceBreakdown";
import PropDetailsDisclaimer from "@/components/bnp-ui/property/PropDetailsDisclaimer";
import PropDetailRealtorCard from "@/components/bnp-ui/property/PropDetailRealtorCard";
import PropDetailsLoading from "@/components/bnp-ui/property/PropDetailsLoading";
import PropertyListingSkeleton from "@/components/bnp-ui/listing-card/PropertyListingSkeleton";
import UrlOptimizedListingCardContainer from "@/components/seo/UrlOptimizedListingCardContainer";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import { resolveReferrerCrumb } from "@/utils/propertyReferrer";
import { getRuntimeEnvironment } from "@/utils/helpers/runtimeEnvironment";
import Link from "next/link";
import {
  buildLocationPath,
  buildPropertyDetailPath,
  propertyStateSlug,
} from "@/lib/seo/property-urls";
import {
  isLandProperty,
  landSizeLabel,
  roomSummary,
} from "@/lib/property-display";
import { getPropertyTitle } from "@/lib/property-seo";


interface PropertyDetailsProps {
  property?: Property;
  error?: string;
}

const runtimeEnvironment = getRuntimeEnvironment()

const PropertyDetails = ({
  property: initialProperty,
}: PropertyDetailsProps) => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const {
    trackPropertyViewed,
    trackPropertySaved,
    trackInspectionPaymentCompleted,
  } = usePostHogAnalytics();
  const [property, setProperty] = useState<Property>(
    initialProperty || ({} as Property),
  );
  const id = (router.query.id as string) || property?.id || "";
  const { from, fromId } = router.query;

  // Middle breadcrumb crumb reflects where the user navigated from
  // (properties / managed / deals / realtor / developer). Direct visits and
  // shared links fall back to Properties; home visits render no middle crumb.
  const referrerCrumb = resolveReferrerCrumb(from, fromId, property);
  const breadcrumbTitle =
    property?.description || property?.address || "Property Details";
  const statePageSlug = property ? propertyStateSlug(property) : null;
  const availabilityLabel = {
    "under-contract": "Under contract",
    "under-review": "Under review",
    sold: "Sold",
    rented: "Rented",
    leased: "Leased",
  }[
    (property?.availability_status || property?.status || "").toLowerCase() as
      | "under-contract"
      | "under-review"
      | "sold"
      | "rented"
      | "leased"
  ];

  const [projects, setProjects] = useState<any[]>([]);

  const { footerAds, headerAds, inFeedAds, popupAds } = usePageAds(AdPage.HOME);

  const validProperties = property?.image_url?.filter((x) => !!x);

  // ADMIN CHECK
  const { user, is_logged_in } = useAuthToken();
  const isAdmin =
    user?.role === "regular-admin" || user?.role === "super-admin";

  // SAVE / SHARE STATE
  const [isSaved, setIsSaved] = useState(false);

  // PAYSTACK LIVE INSPECTION CALLBACK
  const { trxref, paymentSource } = router.query;

  const liveInspectionRequest = async () => {
    const response = await PropertyService.RequestLiveInspection({
      transactionId: trxref as string,
      propertyId: id,
      paymentSource: paymentSource as string,
    });
    return response?.data;
  };

  const paystackMutation = useMutation({
    mutationFn: liveInspectionRequest,
    onSuccess: () => {
      trackInspectionPaymentCompleted({
        inspection_id: trxref as string,
        property_id: id,
        inspection_type: "live_video",
        amount: property?.price || property?.total_package,
        currency: property?.currency || "NGN",
        payment_method: paymentSource as string,
        payment_reference: trxref as string,
        realtor_id: property?.realtor?.id,
        location_city: property?.city,
      });
      toast.success("Live inspection requested!");
    },
    onError: (error: any) => {
      toast.error(parseHttpError(error).message || "Request failed");
    },
  });

  useEffect(() => {
    if (trxref) {
      paystackMutation.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trxref]);

  useEffect(() => {
    if (!id) return;
    // One key (and one delivery) per page-open action: a duplicate effect run —
    // React StrictMode remount, double navigation — reuses the same action and
    // is skipped instead of double-counting the insight.
    const idempotencyKey = startUserAction(buildActionId("page_opened", id));
    if (!idempotencyKey) return;

    PropertyService.TrackPropertyInsight(id, "page_opened", {
      idempotencyKey,
    });
  }, [id]);

  useEffect(() => {
    if (id) {
      track("user_viewed_property", { property_id: id });
    }
  }, [id]);

  // FETCH PROPERTY
  const getPropertyRequest = async () => {
    if (!is_logged_in) {
      const response = await axiosWithoutToken.get(`/properties/${id}`);
      return response?.data;
    }
    try {
      const response = await PropertyService.GetPropertyById({ id });
      return response?.data;
    } catch {
      const response = await axiosWithoutToken.get(`/properties/${id}`);
      return response?.data;
    }
  };

  const { data, isLoading, refetch } = useQuery({
    queryKey: [`property-${id}`, is_logged_in],
    queryFn: getPropertyRequest,
    enabled: !!id,
    initialData: initialProperty ? { data: initialProperty } : undefined,
    initialDataUpdatedAt: 0,
    staleTime: 60 * 60 * 1000,
  });

  useEffect(() => {
    if (data?.data) {
      setProperty(data.data);
    }
  }, [data]);

  useEffect(() => {
    if (!property?.id) return;
    const priceValue = property.price || property.total_package;
    const priceBand =
      priceValue == null
        ? undefined
        : priceValue < 1000000
          ? ("entry" as const)
          : priceValue < 5000000
            ? ("mid" as const)
            : priceValue < 20000000
              ? ("premium" as const)
              : ("luxury" as const);
    const referrer =
      typeof document !== "undefined" ? document.referrer || "direct" : undefined;
    const entrySource =
      !referrer || referrer === "direct"
        ? ("direct" as const)
        : (() => {
            try {
              return new URL(referrer).host === window.location.host
                ? ("internal" as const)
                : ("external" as const);
            } catch {
              return ("external" as const);
            }
          })();
    trackPropertyViewed({
      property_id: property.id,
      property_type: property.property_type,
      listing_type: property.listing_type,
      location_city: property.city,
      price: priceValue,
      price_band: priceBand,
      bedrooms: property.bedroom_count,
      realtor_id: property.realtor?.id,
      developer_id:
        property.createdBy?.role === "developer"
          ? property.createdBy.id
          : undefined,
      is_managed_property: property.property_category === "managed",
      traffic_source: referrer,
      entry_source: entrySource,
    });
  }, [
    property?.id,
    property?.property_type,
    property?.listing_type,
    property?.city,
    property?.price,
    property?.total_package,
    property?.bedroom_count,
    property?.realtor?.id,
    property?.createdBy?.id,
    property?.createdBy?.role,
    property?.property_category,
    trackPropertyViewed,
  ]);

  // FETCH THE LOGGED-IN USER'S SAVED PROPERTIES TO INITIALISE THE SAVE STATE
  const { data: savedPropertiesData } = useQuery({
    queryKey: ["savedProperties"],
    queryFn: async () => {
      const response = await PropertyService.GetSavedProperties();
      return response?.data;
    },
    enabled: is_logged_in,
    staleTime: 60 * 60 * 1000,
  });

  // INITIALISE isSaved
  useEffect(() => {
    if (!is_logged_in) {
      setIsSaved(false);
      return;
    }
    const saved = savedPropertiesData?.data;

    if (Array.isArray(saved)) {
      setIsSaved(saved.some((p: any) => p?.id === id));
    }
  }, [data, savedPropertiesData, id, is_logged_in]);

  // FETCH SIMILAR PROPERTIES
  const { data: similarData, isLoading: similarIsLoading } = useQuery({
    queryKey: [`property-${id}-similar`],
    queryFn: async () => {
      const response = await PropertyService.GetSimilarProperties({
        id,
        page: 1,
        pageSize: 6,
      });
      return response?.data;
    },
    enabled: !!id,
    staleTime: 60 * 60 * 1000,
  });

  const similarProperties: Property[] = similarData?.data ?? [];

  // Similar-properties carousel controls
  const similarTrackRef = useRef<HTMLDivElement>(null);
  const [similarNav, setSimilarNav] = useState({ prev: false, next: false });
  const [similarActive, setSimilarActive] = useState(0);

  const updateSimilarNav = () => {
    const track = similarTrackRef.current;
    if (!track) return;
    setSimilarNav({
      prev: track.scrollLeft > 4,
      next: track.scrollLeft < track.scrollWidth - track.clientWidth - 4,
    });
    const firstCard = track.children[0] as HTMLElement | undefined;
    const step = firstCard ? firstCard.offsetWidth + 20 : track.clientWidth;
    if (step > 0) {
      setSimilarActive(
        Math.max(
          0,
          Math.min(
            Math.round(track.scrollLeft / step),
            Math.max(0, similarProperties.length - 1),
          ),
        ),
      );
    }
  };

  const scrollSimilar = (dir: 1 | -1) => {
    const track = similarTrackRef.current;
    if (!track) return;
    const firstCard = track.children[0] as HTMLElement | undefined;
    const step = firstCard ? firstCard.offsetWidth + 20 : track.clientWidth;
    track.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  const scrollSimilarTo = (index: number) => {
    const track = similarTrackRef.current;
    if (!track) return;
    const firstCard = track.children[0] as HTMLElement | undefined;
    const step = firstCard ? firstCard.offsetWidth + 20 : track.clientWidth;
    const clamped = Math.max(0, Math.min(index, similarProperties.length - 1));
    track.scrollTo({ left: clamped * step, behavior: "smooth" });
  };

  // Refresh carousel arrow state once similar results land
  // (defer to next frame + re-check on resize so mobile correctly enables "next")
  useEffect(() => {
    const raf = requestAnimationFrame(() => updateSimilarNav());
    const t = setTimeout(() => updateSimilarNav(), 300);
    window.addEventListener("resize", updateSimilarNav);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
      window.removeEventListener("resize", updateSimilarNav);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [similarProperties.length]);

  // FETCH PROJECTS
  const { data: projectsData } = useQuery({
    queryKey: ["projects"],
    queryFn: async () => {
      const response = await ProjectService.GetProjects();
      return response?.data;
    },
    enabled: is_logged_in,
    staleTime: 60 * 60 * 1000,
  });

  useEffect(() => {
    if (projectsData) {
      setProjects(projectsData?.data);
    }
  }, [projectsData]);

  // CHECK IF USER HAS REQUESTED LIVE INSPECTION
  const { data: requestedData } = useQuery({
    queryKey: [`has-requested-${id}`, is_logged_in],
    queryFn: async () => {
      const response = await PropertyService.HasRequestedLiveInspection(id);
      return response?.data;
    },
    enabled: !!id && is_logged_in,
  });

  // SAVE / UNSAVE PROPERTY
  const savePropertyMutation = useMutation({
    mutationFn: async (action: "save" | "unsave") => {
      if (action === "unsave") {
        await PropertyService.UnsaveProperty({ propertyId: id });
      } else {
        await PropertyService.SaveProperty({ propertyId: id });
      }
    },
    onSuccess: (_data, action) => {
      setIsSaved(action === "save");
      trackPropertySaved({
        property_id: id,
        property_type: property?.property_type,
        listing_type: property?.listing_type,
        location_city: property?.city,
        price: property?.price || property?.total_package,
        realtor_id: property?.realtor?.id,
        save_action: action === "save" ? "saved" : "unsaved",
      });
      queryClient.invalidateQueries({ queryKey: [`property-${id}`] });
      queryClient.invalidateQueries({ queryKey: ["savedProperties"] });
      if (action === "save") {
        toast.success("Property saved successfully");
      } else {
        toast.success("Property removed from saved properties");
      }
    },
    onError: () => {
      toast.error("Failed to update saved status");
    },
  });

  const handleSave = () => {
    if (!is_logged_in || !user) {
      toast.info("Please log in to save properties");
      return;
    }
    if (isAdmin) {
      toast.info("Please log in as a regular user to save properties");
      return;
    }
    savePropertyMutation.mutate(isSaved ? "unsave" : "save");
  };

  const { formatPrice } = useCurrency();
  const handleShare = async () => {
    // Canonical URL: sharing `/property/{id}` costs the crawler a 301 hop
    // before it reaches the page whose metadata it needs.
    const url = property
      ? `${window.location.origin}${buildPropertyDetailPath(property)}`
      : `${window.location.origin}/property/${id}`;

    const listingType = property?.listing_type || "Property";
    const formattedListingType =
      listingType === "sale"
        ? "For Sale"
        : listingType === "rent"
          ? "For Rent"
          : listingType === "shortlet"
            ? "Shortlet"
            : capitalizeFirstLetter(listingType);
    const location = [property?.city, property?.state]
      .filter(Boolean)
      .join(", ");
    const price = property?.price || property?.total_package;
    const formattedPrice = price ? formatPrice(price, property?.currency) : "";

    const parts = [formattedListingType];
    if (location) parts.push(`in ${location}`);
    if (formattedPrice) parts.push(`for ${formattedPrice}`);

    // Category-gated and > 0 only: land/commercial records are saved with a
    // numeric bedroom_count of 0, which used to render as "(0 beds, 0 baths)".
    const rooms = property ? roomSummary(property) : "";
    if (rooms) {
      parts.push(`(${rooms})`);
    }

    if (property?.furnished) {
      parts.push("Fully furnished");
    }

    if (property?.is_negotiable) {
      parts.push("Negotiable");
    }

    if (property && isLandProperty(property)) {
      const size = landSizeLabel(property);
      if (size) parts.push(size);
    }

    const address = [property?.address, property?.city, property?.state]
      .filter(Boolean)
      .join(", ");
    if (address) {
      parts.push(`at ${address}`);
    }

    const shareData = {
      title: property ? getPropertyTitle(property) : "Property Details",
      text: parts.join(" "),
      url: url,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        // User cancelled
      }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    }
  };

  return (
    <>
      <div className="font-sans w-[100%] mx-auto">
        <div className="w-[95vw] lg:w-full flex justify-center mx-auto pt-8">
          <Nav />
        </div>
        {headerAds[0] && (
          <div className="w-full max-w-[1280px] mx-auto px-4 pt-2 pb-1">
            <AdRenderer ad={headerAds[0]} placement={AdPlacement.HEADER} />
          </div>
        )}
        <div className="mx-auto w-[100%] lg:w-[1280px]">
          <Breadcrumb
            items={[
              { label: "Home", href: "/" },
              ...(referrerCrumb ? [referrerCrumb] : []),
              {
                label: breadcrumbTitle,
              },
            ]}
            className="mb-5 pt-8 px-4 lg:px-[84px]"
          />
          {property?.state && statePageSlug && !runtimeEnvironment.isProduction && (
            <div className="px-4 text-sm lg:px-[84px]">
              <Link
                className="text-teal-800 underline"
                href={buildLocationPath(statePageSlug)}
              >
                Browse properties in {property.state}
              </Link>
            </div>
          )}

          <div className="px-4 lg:px-[84px] py-[30px] pb-16">
            {isLoading && <PropDetailsLoading />}

            {!isLoading && data && property && (
              <div>
                {availabilityLabel && (
                  <p className="mb-4 inline-block rounded bg-amber-100 px-3 py-1 text-sm font-semibold text-amber-900">
                    Listing status: {availabilityLabel}
                  </p>
                )}
                {/* TITLE SECTION + LOCATION & LISTED DATE */}
                <PropDetailHeading property={property} />

                {/* In-feed ad */}
                {inFeedAds[0] && (
                  <div className="w-full py-5">
                    <div className="mx-auto w-full max-w-[1200px]">
                      <AdRenderer
                        ad={inFeedAds[0]}
                        placement={AdPlacement.IN_FEED}
                      />
                    </div>
                  </div>
                )}

                {/* ================================================================= */}
                {/* MOBILE LAYOUT (CSS Order Applied for Fast Mobile Conversion) */}
                {/* ================================================================= */}
                <div className="flex flex-col gap-6 block lg:hidden">
                  {/* 1. Media/Gallery */}
                  <div className="order-1">
                    <PropDetailsMedia allImages={validProperties || []} />
                  </div>

                  {/* 2. Title, Badges, Bed/Bath Semantics */}
                  <div className="order-2">
                    <PropDetailsSemantics
                      property={property}
                      isSaved={isSaved}
                      onShare={handleShare}
                      onSave={handleSave}
                    />
                  </div>

                  {/* 3. Realtor / Primary Action Card */}
                  <div className="order-3">
                    <PropDetailRealtorCard
                      property={property}
                      realtor={property?.realtor}
                      onViewProperties={() =>
                        router.push(
                          `/realtors/${property?.realtor?.slug || property?.realtor?.id}`,
                        )
                      }
                      hasRequested={requestedData?.data?.hasRequested}
                    />
                  </div>

                  {/* 4. Price Breakdown */}
                  <div className="order-4">
                    <PropDetailsPriceBreakdown property={property} />
                  </div>

                  {/* 5. Amenities */}
                  <div className="order-5">
                    <PropDetailsAmenities amenities={property?.amenities || []} />
                  </div>

                  {/* 6. Additional Info */}
                  <div className="order-6">
                    <PropDetailsAdditionalInfo property={property} />
                  </div>

                  {/* 7. Map Location */}
                  <div className="order-7">
                    <PropDetailsMap property={property} />
                  </div>

                  {/* 8. Disclaimer */}
                  <div className="order-8">
                    <PropDetailsDisclaimer property={property} />
                  </div>

                  {/* 9. Ad Slot */}
                  <div className="order-9 mt-2">
                    <GoogleAd
                      adSlot={process.env.NEXT_PUBLIC_AD_SLOT_SIDEBAR || ""}
                      adFormat="rectangle"
                      className="w-full"
                    />
                  </div>
                </div>

                {/* ================================================================= */}
                {/* DESKTOP LAYOUT (Visible on lg screens and up) */}
                {/* ================================================================= */}
                <div className="hidden lg:grid grid-cols-12 gap-10 lg:gap-12 items-start max-w-7xl margin-auto">
                  {/* MAIN CONTENT COLUMN */}
                  <div className="lg:col-span-7 xl:col-span-8 min-w-0 space-y-6">
                    <PropDetailsMedia allImages={validProperties || []} />

                    <PropDetailsSemantics
                      property={property}
                      isSaved={isSaved}
                      onShare={handleShare}
                      onSave={handleSave}
                    />

                    <PropDetailsAmenities
                      amenities={property?.amenities || []}
                    />

                    <PropDetailsAdditionalInfo property={property} />

                    <PropDetailsMap property={property} />
                  </div>

                  {/* SIDEBAR COLUMN */}
                  <div className="lg:col-span-5 xl:col-span-4 w-full space-y-6">
                    <PropDetailRealtorCard
                      property={property}
                      realtor={property?.realtor}
                      onViewProperties={() =>
                        router.push(
                          `/realtors/${property?.realtor?.slug || property?.realtor?.id}`,
                        )
                      }
                      hasRequested={requestedData?.data?.hasRequested}
                    />

                    <PropDetailsPriceBreakdown property={property} />

                    <PropDetailsDisclaimer property={property} />

                    <div className="mt-6">
                      <GoogleAd
                        adSlot={process.env.NEXT_PUBLIC_AD_SLOT_SIDEBAR || ""}
                        adFormat="rectangle"
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {footerAds[0] && (
          <div className="max-w-[1280px] mx-auto px-4 py-6">
            <AdRenderer ad={footerAds[0]} placement={AdPlacement.FOOTER} />
          </div>
        )}

        {/* ================= SIMILAR PROPERTIES ================= */}
        {!isLoading && property?.id && (similarIsLoading || similarProperties.length > 0) && (
          <div className="mx-auto w-[100%] lg:w-[1280px]">
            <div className="px-4 lg:px-[84px] pb-16">
              <div className="flex items-end justify-between gap-4 mb-6">
                <div className="flex flex-col gap-2">
                  <h2 className="text-slate-900 font-bold text-[20px] lg:text-[28px]">
                    Similar Properties
                  </h2>
                  <p className="text-slate-500 font-normal text-sm lg:text-base">
                    You may also like these properties
                  </p>
                </div>

                {!similarIsLoading && similarProperties.length > 1 && (
                  <div className="flex items-center gap-3 shrink-0">
                    <span
                      aria-live="polite"
                      className="hidden sm:inline-flex items-center rounded-full bg-[#19545e]/10 px-3 py-1 text-xs font-semibold text-[#19545e]"
                    >
                      {similarActive + 1} / {similarProperties.length}
                    </span>
                    <button
                      type="button"
                      onClick={() => scrollSimilar(-1)}
                      disabled={!similarNav.prev}
                      aria-label="Previous properties"
                      className="flex h-11 w-11 lg:h-12 lg:w-12 items-center justify-center rounded-full border-2 border-[#19545e] bg-[#19545e] text-white shadow-lg transition-all hover:bg-[#143f47] hover:border-[#143f47] hover:shadow-xl active:scale-95 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400 disabled:shadow-none disabled:opacity-60"
                    >
                      <IconChevronLeft className="h-6 w-6" strokeWidth={2.5} />
                    </button>
                    <button
                      type="button"
                      onClick={() => scrollSimilar(1)}
                      disabled={!similarNav.next}
                      aria-label="Next properties"
                      className="flex h-11 w-11 lg:h-12 lg:w-12 items-center justify-center rounded-full border-2 border-[#19545e] bg-[#19545e] text-white shadow-lg transition-all hover:bg-[#143f47] hover:border-[#143f47] hover:shadow-xl active:scale-95 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400 disabled:shadow-none disabled:opacity-60"
                    >
                      <IconChevronRight className="h-6 w-6" strokeWidth={2.5} />
                    </button>
                  </div>
                )}
              </div>

              {similarIsLoading ? (
                <PropertyListingSkeleton />
              ) : (
                <>
                  <div className="relative">
                    <div
                      ref={similarTrackRef}
                      onScroll={updateSimilarNav}
                      className="flex gap-5 overflow-x-auto snap-x snap-mandatory pb-2 cursor-grab active:cursor-grabbing [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                    >
                      {similarProperties.map((similar: Property) => (
                        <div key={similar.id} className="snap-start shrink-0">
                          <UrlOptimizedListingCardContainer
                            property={similar}
                          />
                        </div>
                      ))}
                    </div>
                    {/* Edge fades to signal more content */}
                    <div
                      aria-hidden="true"
                      className={`pointer-events-none absolute inset-y-0 left-0 w-8 ${similarNav.prev ? "opacity-100" : "opacity-0"}`}
                    />
                    <div
                      aria-hidden="true"
                      className={`pointer-events-none absolute inset-y-0 right-0 w-8  ${similarNav.next ? "opacity-100" : "opacity-0"}`}
                    />
                  </div>

                  {similarProperties.length > 1 && (
                    <div className="mt-4 flex flex-col items-center gap-3">
                      <div className="flex items-center justify-center gap-3">
                        <span className="text-xs font-medium text-slate-500 sm:hidden">
                          {similarActive + 1} / {similarProperties.length}
                        </span>
                        <div className="flex items-center gap-2">
                          {similarProperties.map((similar: Property, index: number) => (
                            <button
                              key={similar.id}
                              type="button"
                              onClick={() => scrollSimilarTo(index)}
                              aria-label={`Go to property ${index + 1}`}
                              className={`h-2.5 rounded-full transition-all ${
                                index === similarActive
                                  ? "w-8 bg-[#19545e]"
                                  : "w-2.5 bg-slate-300 hover:bg-slate-400"
                              }`}
                            />
                          ))}
                        </div>
                        <span className="hidden sm:inline text-xs font-medium text-slate-500">
                          Swipe or use arrows to explore
                        </span>
                      </div>
                      {/* Mobile-only swipe CTA */}
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-[#19545e]/10 px-3 py-1.5 text-xs font-semibold text-[#19545e] sm:hidden">
                        Swipe to view more
                        <span className="flex items-center">
                          <IconChevronRight className="h-4 w-4 animate-pulse" strokeWidth={2.5} />
                          <IconChevronRight className="-ml-2.5 h-4 w-4 animate-pulse" strokeWidth={2.5} />
                        </span>
                      </span>
                    </div>
                  )}

                  <div className="mt-8 flex justify-center">
                    <Button
                      onClick={() => router.push("/properties")}
                      className="bg-bnp-cyan font-sans text-white text-sm font-semibold w-full sm:w-[275px] h-[52px] rounded-lg"
                    >
                      Explore All Properties
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {popupAds[0] && (
          <AdRenderer ad={popupAds[0]} placement={AdPlacement.POPUP} />
        )}

        <Footer />
      </div>
    </>
  );
};

export default PropertyDetails;